// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useState } from 'react';
import { Alert, Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-react-v3/polaris';
import { Messages } from '../../Common/ErrorMessages';
import { ApiError } from '../../../utils/serverErrors';
import * as goldenDatasets from './goldenDatasets';

interface DeleteDatasetModalProps {
  apiClient: { httpDelete: (path: string, body?: object) => Promise<unknown> };
  dataset: goldenDatasets.GoldenDatasetSummary;
  onCancel: () => void;
  onDeleted: () => void;
}

/** Asks before deleting a dataset at its shown version; a failed delete is shown in the dialog. */
const DeleteDatasetModal = ({
  apiClient,
  dataset,
  onCancel,
  onDeleted,
}: DeleteDatasetModalProps): JSX.Element => {
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const confirm = (): void => {
    setSubmitting(true);
    setErrors([]);
    apiClient.httpDelete(goldenDatasets.deletePath(dataset)).then(onDeleted, (error: ApiError) => {
      setSubmitting(false);
      setErrors(goldenDatasets.describeWriteError(error));
    });
  };

  return (
    <Modal
      id="deleteGoldenDatasetModal"
      visible
      closeAriaLabel="Close"
      onDismiss={(): void => {
        if (!submitting) {
          onCancel();
        }
      }}
      header={`Delete ${dataset.name}?`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              id="cancelDeleteGoldenDataset"
              variant="link"
              disabled={submitting}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              id="confirmDeleteGoldenDataset"
              variant="primary"
              loading={submitting}
              onClick={confirm}
            >
              Delete
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {errors.length > 0 && (
          <Alert id="deleteGoldenDatasetErrors" type="error">
            <Messages messages={errors} />
          </Alert>
        )}
        <div>
          It is removed from the list, and its pricing and scoping model pair becomes free for
          another dataset. Its version history is kept for audit.
        </div>
      </SpaceBetween>
    </Modal>
  );
};

export default DeleteDatasetModal;
